import { useMemo, useEffect, useState, useContext, useRef } from 'react';
import isEmpty from 'lodash.isempty';

// types
import type { SlidingTabsProps } from '../sliding-tabs/sliding-tabs.types';

// styles
import styles from './tabs.module.scss';

// components
import TabView from './tab-view';
import { UPDATE_ACCORDION_STATE, EditingContext } from 'components/ContentCreator/EditingContext';
import { SlidingTabs } from 'components/ContentElementsGdds/sliding-tabs/sliding-tabs';

// utils
import { classNameBuilder } from 'utils/classNameBuilder';
import { useTracking } from 'utils/hooks/useTracking';
import { useTracking as useTracking2 } from 'utils/tracking/track';
import { tabClick } from 'components/ContentElements/tabs/tracking-actions';
import { useHash } from 'utils/hooks/useHash';
import useTouchDetector from 'utils/hooks/use-touch-detector';
import { generateSlug } from 'utils/slug';
import hasSomeParentTheClass from 'utils/hasSomeParentTheClass';
import { useIsCC } from 'utils/hooks/use-is-cc';
import { useIsMobile } from 'components/App/SizeProvider';
import { useIsPreview } from 'utils/hooks/useIsPreview';
import { decodingContent } from 'utils/decodingContent';
import { useXy } from 'utils/hooks/use-xy';

export interface GddsTabsProps {
  tabs: any[];
  title: string;
  subtitle: string;
  anchor: string;
  titleInAnchor: boolean;
  background: string;
  catalogName?: string;
  contentIndex?: number;
  content?: string;
  position?: string;
  spacing?: {
    top?: string;
    bottom?: string;
  };
}

export function GddsTabs(props: Readonly<GddsTabsProps | SlidingTabsProps>) {
  const {
    tabs,
    catalogName,
    title,
    subtitle,
    anchor,
    titleInAnchor,
    contentIndex,
    content,
    background,
    position,
    spacing,
  } = props as GddsTabsProps;
  // We want scroll to section (hash) only on first page load.
  // If the user otherwise changes the tab, we don't want to scroll to the section
  const shouldScrollToHash = useRef(true);
  const isMobile = useIsMobile();
  const track = useTracking();
  const { trackClick } = useTracking2();
  const isCC = useIsCC();
  const isXy = useXy();
  const isPreview = useIsPreview();
  const { state: editingState, dispatch } = useContext(EditingContext);
  const index = Number(editingState[tabs[0]?.previewId]) || 0;
  const isTouch = useTouchDetector();
  const [currentIndex, setCurrentIndex] = useState(isCC && index);
  const ref = useRef<HTMLDivElement | null>(null);
  const dropdownTitles = useMemo(() => {
    return tabs.map((tab, i) => ({
      id: tab.buttonText,
      value: decodingContent(tab.buttonText),
      text: decodingContent(tab.buttonText),
      index: i,
    }));
  }, [tabs]);

  const tabTitles = useMemo(() => {
    if (isMobile) {
      return dropdownTitles.map((i) => i.id);
    } else {
      return dropdownTitles.map((i) => i.text);
    }
  }, [dropdownTitles, isMobile]);

  const { matchIndex, hashes } = useHash(tabTitles);

  useEffect(() => {
    if (!isEmpty(tabTitles) && matchIndex > -1) {
      setCurrentIndex(matchIndex);
    } else if (matchIndex === -1) {
      setCurrentIndex(0);
    }
  }, [matchIndex, tabTitles]);

  // deeplink logik
  useEffect(() => {
    if (hashes?.[hashes?.length - 1] === generateSlug(tabTitles[matchIndex])) {
      setTimeout(() => {
        const posY = ref.current?.offsetTop ?? 0;
        if (shouldScrollToHash.current) {
          window.scroll({ top: posY - 138, behavior: 'smooth' });
        }
      }, 200);
    }
  }, [hashes, matchIndex, tabTitles]);

  const onChange = (item) => {
    shouldScrollToHash.current = false;
    const value = isTouch ? item.index || item.value : item.value || item.index;
    const number = Number(value) || Number(item.index);
    track.trackEvent(tabClick(tabs[number].buttonText));
    trackClick({
      click_intent: 'more_info',
      click_element: 'tab',
      click_text: tabs[index].buttonText,
    });

    if (isPreview) {
      dispatch({
        type: UPDATE_ACCORDION_STATE,
        accordionIndex: value,
        previewId: tabs[value || 0]?.previewId,
      });
    }
    const hasTabsAsParent = hasSomeParentTheClass(ref?.current, styles.tabs, true);

    const firstHashName = hasTabsAsParent
      ? `${window.location.hash.split('|')[0]?.substring(1)}|`
      : '';

    window.location.hash = `#${firstHashName}${generateSlug(
      item.title || item.text?.toUpperCase(),
    )}`;

    setCurrentIndex(number);
  };

  if (isEmpty(tabs) || isEmpty(tabTitles)) return null;

  if (isXy) {
    return <SlidingTabs {...(props as SlidingTabsProps)} />;
  }

  return (
    <div
      className={classNameBuilder(
        styles.tabs,
        styles[background],
        spacing?.top === 'small' && styles.paddingTopSmall,
        spacing?.bottom === 'small' && styles.paddingBottomSmall,
      )}
      ref={ref}
    >
      <TabView
        title={title}
        subtitle={subtitle}
        anchor={anchor}
        titleInAnchor={titleInAnchor}
        catalogName={catalogName ?? ''}
        contentIndex={contentIndex}
        dropdownTitles={dropdownTitles}
        tabTitles={tabTitles}
        onChange={onChange}
        isMobile={isMobile}
        tabsContent={tabs}
        currentIndex={currentIndex || 0}
        description={content}
        position={position}
      />
    </div>
  );
}
